import { useToast } from "@agney/ir-toast";
import React, { useCallback, useEffect, useRef } from "react";
import { pushNotificationService } from "../../../services/PushNotification.service";
import { AuthService } from "../../../services/authService";

/**
 * Signs the user out after a period with no interaction.
 *
 * The app had no inactivity timeout at all, and the DRF tokens it uses never
 * expire on their own, so a browser left open on a shared ward workstation
 * stayed authenticated to a patient-data system indefinitely. Anyone walking
 * up to it had the previous user's session.
 *
 * Mounted inside the authenticated shell, so it never runs on the login screen.
 *
 * To change how long a session may sit idle, change IDLE_LIMIT_MS below.
 */
const IDLE_LIMIT_MS = 15 * 60 * 1000;
const WARN_BEFORE_MS = 60 * 1000;

// Anything the user does deliberately counts as activity. Scroll and
// touchmove are included because reading a long status board is work.
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "touchstart",
  "touchmove",
  "scroll",
  "click",
];

const IdleTimeout: React.FC = () => {
  const Toast = useToast();
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const signOut = useCallback(async () => {
    await pushNotificationService.deleteToken();
    AuthService.logout();
  }, []);

  const resetTimers = useCallback(() => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);

    warnTimer.current = setTimeout(() => {
      Toast.error("You will be signed out shortly because of inactivity.");
    }, IDLE_LIMIT_MS - WARN_BEFORE_MS);

    logoutTimer.current = setTimeout(() => {
      void signOut();
    }, IDLE_LIMIT_MS);
  }, [Toast, signOut]);

  useEffect(() => {
    resetTimers();

    // Passive: these listeners only observe, and marking them so keeps them
    // off the critical path of scrolling.
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, resetTimers, { passive: true })
    );

    return () => {
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, resetTimers)
      );
      if (warnTimer.current) clearTimeout(warnTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };
  }, [resetTimers]);

  return null;
};

export default IdleTimeout;
